import React, {
  memo,
  type ReactElement,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Box,
  ButtonSelect,
  Icon,
  InlineMessage,
  InputField,
  Link as DSLink,
  Menu,
  MenuItem,
  type MenuOption,
  Typography,
} from '@saviynt/design-system';
import cx from 'classnames';

import {
  CF_NEU_BLD,
  CF_SEC_MED,
  DEBOUNCE_TIME_FILTER_SEARCH,
  VIOLATIONS_TAB_LIST_AD_FILTER,
} from '../../../constants';
import { useDebounceForFunctions, useInvertedLocalize } from '../../../hooks';

import { CUSTOM_INPUTS, RISK_CUSTOM_LABEL_KEY } from './constants';
import { type SodCustomFilterProps } from './SodCustomFilter';
import {
  getRiskFilterOption,
  isLimitFollowed,
  isOrderFollowed,
  isStrictNumeric,
} from './utils';

import './SodRiskScoreFilter.css';

const DEFAULT_CRITICAL: Record<string, string | boolean> = {
  state: false,
  message: '',
} as const;

interface FilterRefType extends HTMLDivElement {
  openDropdown: () => void;
}

export interface OptionsMap {
  options: MenuOption[];
  custom: {
    [key in (typeof CUSTOM_INPUTS)[keyof typeof CUSTOM_INPUTS]]: string;
  };
}

/**
 * SodRiskScoreFilter component allows users to filter SOD risk scores using a combination
 * of predefined options and a range input.
 *
 * @example
 * const options = [
 *   { label: 'Low Risk', value: 'low', isChecked: false },
 *   { label: 'Medium Risk', value: 'medium', isChecked: false },
 *   { label: 'High Risk', value: 'high', isChecked: false }
 * ];
 *
 * const handleOptionsClick = (filterKey, method, updatedOptions) => {
 *   console.log(filterKey, method, updatedOptions);
 * };
 *
 * return (
 *   <SodRiskScoreFilter
 *     filterKey="riskScore"
 *     method="BETWEEN"
 *     options={options}
 *     activeOptions={activeOptions}
 *     handleOptionsClick={handleOptionsClick}
 *     valueStart="0"
 *     valueEnd="100"
 *   />
 * );
 *
 * @param {SodRiskScoreFilterProps} props - The component props
 * @returns {React.ReactElement} A risk score filter component with options and range inputs
 */
function SodRiskScoreFilter({
  filter,
  activeOptions,
  footerLink1Text,
  footerLink2Text,
  enabledFilterValue,
  setEnabledFilterValue,
  handleOptionsClick,
  handleFilterChange,
  isDisabled,
}: SodCustomFilterProps): ReactElement {
  const l = useInvertedLocalize();

  const [isClearBtnDisabled, setIsClearBtnDisabled] = useState(true);
  const [optionsMap, setOptionsMap] = useState<OptionsMap>({
    options: filter.options ?? [],
    custom: {
      [CUSTOM_INPUTS.START]: '',
      [CUSTOM_INPUTS.END]: '',
    },
  });
  const [inputCritical, setInputCritical] = useState(DEFAULT_CRITICAL);

  const filterRef = useRef<FilterRefType | null>(null);

  useEffect(() => {
    const shouldOpenDropdown = Boolean(
      filterRef.current !== null &&
        enabledFilterValue &&
        filter.value === enabledFilterValue
    );

    if (shouldOpenDropdown && filterRef.current) {
      setTimeout(() => {
        filterRef.current?.openDropdown();

        if (setEnabledFilterValue) {
          setEnabledFilterValue(null);
        }
      }, 200);
    }
  }, [filter.value, enabledFilterValue, setEnabledFilterValue]);

  // Determine if  `Clear Selection` is enabled or not
  useEffect(() => {
    const isOptionTrue = optionsMap.options.some((option) => option.isChecked);
    const isCustomOption = Object.values(optionsMap.custom).some(
      (customIp) => customIp !== ''
    );

    if (!isOptionTrue && !isCustomOption) {
      setIsClearBtnDisabled(true);

      return;
    }

    setIsClearBtnDisabled(false);
  }, [optionsMap]);

  // Manipulate options based on applied filters
  useEffect(() => {
    const customOptions: (number | null)[] = JSON.parse(
      activeOptions.find((activeOption) =>
        activeOption.label.includes(RISK_CUSTOM_LABEL_KEY)
      )?.value ?? '[null, null]'
    ) as (number | null)[];

    const [customStartValue, customEndValue] = customOptions;

    setOptionsMap((prev) => ({
      ...prev,
      options: prev.options.map((option) => ({
        ...option,
        isChecked: activeOptions.some(
          (activeOption) => activeOption.value === option.value
        ),
      })),
      custom: {
        [CUSTOM_INPUTS.START]: String(
          customStartValue === null ? '' : customStartValue
        ),
        [CUSTOM_INPUTS.END]: String(
          customEndValue === null ? '' : customEndValue
        ),
      },
    }));
  }, [activeOptions]);

  const handleOptionsForCustomIp = useDebounceForFunctions(
    <T extends string | number | null>(start: T, end: T) => {
      const { label, value } = getRiskFilterOption(start, end);

      handleOptionsClick(filter.value, filter.method, [
        {
          label,
          value,
          isChecked: true,
        },
      ]);
    },
    DEBOUNCE_TIME_FILTER_SEARCH
  );

  const handleTextChange = <T extends string>(name: T, value: T) => {
    setInputCritical(() => DEFAULT_CRITICAL);

    const isNumber = isStrictNumeric(value);

    if (!isNumber) {
      setInputCritical(() => ({
        state: true,
        message: 'Value should be a number',
      }));

      return;
    }

    const numberInput = Number(value);
    const isLimit = isLimitFollowed(numberInput);

    if (!isLimit) {
      setInputCritical(() => ({
        state: true,
        message: 'Value should be greater than 0',
      }));

      return;
    }

    const isOrder = isOrderFollowed(name, numberInput, optionsMap);

    if (!isOrder) {
      setInputCritical({
        state: true,
        message: 'Start value should be less than end value',
      });
    }

    setOptionsMap((prev) => ({
      ...prev,
      custom: {
        ...prev.custom,
        [name]: value,
      },
    }));

    if (name === CUSTOM_INPUTS.START) {
      handleOptionsForCustomIp(value, optionsMap.custom[CUSTOM_INPUTS.END]);

      return;
    }

    handleOptionsForCustomIp(optionsMap.custom[CUSTOM_INPUTS.START], value);
  };

  const customLabel = useMemo(() => {
    const { label } = filter;

    if (activeOptions.length !== 0) {
      const labelArray = activeOptions.map((option) => option.label).join(', ');

      return (
        <Typography
          kind='body2'
          desktop='body2-d'
          mobile='body2-d'
          color={CF_SEC_MED}>
          <span className='SodCustomFilter-btnLabel'>{label}</span>:&nbsp;
          {labelArray}
        </Typography>
      );
    }

    return label;
  }, [activeOptions, filter]);

  const isViolationAgeFilter = Boolean(
    filter.value === VIOLATIONS_TAB_LIST_AD_FILTER.VIOLATIONAGE.value
  );

  const inputWrapperClasses = cx(
    'SodRiskScoreFilter-inputFieldWrapper',
    isViolationAgeFilter && 'SodRiskScoreFilter-inputFieldWrapper--isAgeFilter'
  );

  return (
    <Menu
      ref={filterRef}
      key={filter.value}
      CustomMenu={
        <>
          <ul role='listbox' tabIndex={-1} className='MenuMulti-items'>
            {optionsMap.options.map((option) => (
              <MenuItem
                key={option.value}
                option={{
                  label: option.label,
                  value: option.value,
                  isChecked: option.isChecked,
                }}
                onClick={() => {
                  setInputCritical(DEFAULT_CRITICAL);
                  handleOptionsClick(filter.value, filter.method, [
                    {
                      label: option.label,
                      value: option.value,
                      isChecked: true,
                    },
                  ]);
                }}
                tabIndex={0}
              />
            ))}
            <Box tag='div' className={inputWrapperClasses}>
              <InputField
                name={CUSTOM_INPUTS.START}
                placeholder={l('Type number')}
                kind='search'
                backgroundColor='neutral'
                size='medium'
                value={optionsMap.custom[CUSTOM_INPUTS.START]}
                setValue={(text) => {
                  handleTextChange(CUSTOM_INPUTS.START, text);
                }}
                shouldDisallowBrackets
                isResetButtonVisible={false}
                className='SodRiskScoreFilter-inputField'
              />
              <Typography
                kind='body3'
                desktop='body3-d'
                mobile='body3-d'
                color={CF_NEU_BLD}>
                {l('to')}
              </Typography>
              <InputField
                name={CUSTOM_INPUTS.END}
                placeholder={l('Type number')}
                kind='search'
                backgroundColor='neutral'
                size='medium'
                value={optionsMap.custom[CUSTOM_INPUTS.END]}
                setValue={(text) => {
                  handleTextChange(CUSTOM_INPUTS.END, text);
                }}
                shouldDisallowBrackets
                isResetButtonVisible={false}
                className='SodRiskScoreFilter-inputField'
              />

              {isViolationAgeFilter && (
                <Typography
                  kind='body2'
                  desktop='body2-d'
                  mobile='body2-d'
                  color={CF_NEU_BLD}>
                  {l('days old')}
                </Typography>
              )}
            </Box>
            {inputCritical.state ? (
              <InlineMessage
                text={inputCritical.message as string}
                className='SodRiskScoreFilter-warning'
                colorTheme='critical'
                size='medium'
                leftIcon={<Icon kind='AlertCriticalFilled' />}
              />
            ) : null}
          </ul>
          <Box tag='div' className='SodRiskScoreFilter-footer'>
            <DSLink
              text={footerLink1Text}
              size='small'
              kind='button'
              onClick={() => {
                handleFilterChange({
                  value: filter.value,
                  isChecked: true,
                  method: filter.method,
                });
              }}
            />
            <DSLink
              text={footerLink2Text}
              isDisabled={isClearBtnDisabled}
              size='small'
              kind='button'
              onClick={() => {
                handleOptionsClick(filter.value, filter.method, []);
              }}
            />
          </Box>
        </>
      }
      label={filter.label}
      trigger={
        <ButtonSelect kind='pill' label={customLabel} isDisabled={isDisabled} />
      }
    />
  );
}

SodRiskScoreFilter.defaultProps = {
  enabledFilterValue: null,
  setEnabledFilterValue: () => {},
  isDisabled: false,
};

export default memo(SodRiskScoreFilter);
